import React from 'react';
import { Timer } from './Timer';
import { MarketOutcome } from '../../types/market';

// Default image when market has no image
const DEFAULT_MARKET_IMAGE = '/assets/icons/imo-evergreen-floating.png';

interface MarketHeaderProps {
  question: string;
  rawQuestion?: string; // Full question for tooltip
  imageUrl: string | null;
  endsAt: Date;
  totalDeposits?: number; // in SOL
  totalParticipants?: number;
  outcome?: MarketOutcome; // Finalized end-state (for ended markets)
}

export const MarketHeader: React.FC<MarketHeaderProps> = ({ 
  question,
  rawQuestion,
  imageUrl,
  endsAt, 
  totalDeposits = 0, 
  totalParticipants = 0,
  outcome
}) => {
  // Use default image if none provided
  const displayImage = imageUrl || DEFAULT_MARKET_IMAGE;
  const isDefaultImage = !imageUrl;

  // Format volume display
  const formatVolume = (amount: number): string => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount.toFixed(2);
  };

  return (
    <div className="market-header-hero">
      {/* Image section - left side */}
      <div className="market-hero-section">
        <div className={`market-hero-image ${isDefaultImage ? 'default-image' : ''}`}>
          <img 
            src={displayImage} 
            alt="" 
            onError={(e) => {
              // Fallback to default if image fails to load
              (e.target as HTMLImageElement).src = DEFAULT_MARKET_IMAGE;
              (e.target as HTMLImageElement).parentElement?.classList.add('default-image');
            }}
          />
        </div>
      </div>

      {/* Content section - right side */}
      <div className="market-content-section">
        {/* Volume stat - top */}
        <div className="market-volume">
          <img 
            src="/assets/icons/solanaLogoMark.svg" 
            alt="SOL" 
            className="volume-sol-icon"
          />
          <span className="volume-amount">{formatVolume(totalDeposits)} VOL</span>
        </div>

        {/* Question text with stats - middle */}
        <div className="market-question-text">
          <h2 title={rawQuestion || question}>{question}</h2>
          
          {/* Timer and stats - nested under question */}
          <div className="market-stats">
            {outcome ? (
              <>
                <span className="market-stat market-stat-ended">Ended</span>
                <span className="market-stat-separator">|</span>
                <span className="market-stat market-stat-opinions">
                  <span className="opinions-number">{totalParticipants.toLocaleString()}</span> casted
                </span>
              </>
            ) : (
              <>
                <Timer endsAt={endsAt} />
                <span className="market-stat-separator">|</span>
                <span className="market-stat market-stat-opinions">
                  <span className="opinions-number">{totalParticipants.toLocaleString()}</span> casted
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
